<template>
  <div>
    <empty-state
      v-if="!restaurantIds"
      icon="mdi-silverware"
      :title="$t('select_restaurant')"
      :subtitle="$t('please_select_restaurant_to_view_data')"
    />

    <report-table
      v-else
      :data="filteredData"
      :loading="isLoading"
      :search-query="searchQuery"
      :changing-status-order-id="changingStatusOrderId"
      @show-details="showOrderDetails($event.order, $event.guest)"
      @change-status="changeOrderStatus($event.order, $event.guest, $event.newStatus)"
    />

    <!-- Order Details Dialog -->
    <guest-order-details-dialog
      v-model="orderDetailsDialog"
      :order="selectedOrder"
      :guest="selectedGuest"
      :changing-status-order-id="changingStatusOrderId"
      @change-status="changeOrderStatus($event.order, $event.guest, $event.newStatus)"
    />
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import ReportTable from "@/components/guest/ReportTable.vue";
import reportService from "@/services/report";
import GuestOrderDetailsDialog from "@/components/dialogs/GuestOrderDetailsDialog.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "AdminReportTab",
  components: {
    EmptyState,
    ReportTable,
    GuestOrderDetailsDialog,
  },
  mixins: [hasSnackbar, hasPrinting, hasExcel],
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
    dateRange: {
      type: Array,
      default: () => [
        new Date().toISOString().substr(0, 10),
        new Date().toISOString().substr(0, 10),
      ],
    },
    selectedStatus: {
      type: Object,
      default: () => ({ id: 0, name: "all" }),
    },
    selectedDeliveryType: {
      type: Object,
      default: () => ({ id: 3, name: "all" }),
    },
    selectedRestaurants: Array,
    restaurantIds: String,
    isActiveTab: Boolean,
  },
  data() {
    return {
      reportData: {},
      companies: [],
      isLoading: false,
      orderDetailsDialog: false,
      selectedOrder: null,
      selectedGuest: null,
      changingStatusOrderId: null,
    };
  },
  computed: {
    restaurantNames() {
      if (this.selectedRestaurants && this.selectedRestaurants.length > 0) {
        return this.selectedRestaurants.map((r) => r.name).join(", ");
      }
      return "Admin";
    },
    restaurantSummary() {
      if (!this.selectedRestaurants || this.selectedRestaurants.length === 0) {
        return "";
      }
      const total = this.selectedRestaurants.length;
      return `${this.$t("restaurants")} (${total})`;
    },
    restaurantNamesForFooter() {
      if (!this.selectedRestaurants || this.selectedRestaurants.length === 0) {
        return "";
      }
      return this.selectedRestaurants.map((r) => r.name).join(", ");
    },
    companySummary() {
      if (!this.companies || this.companies.length === 0) {
        return "";
      }
      const total = this.companies.length;
      return `${this.$t("companies")} (${total})`;
    },
    companyNamesForFooter() {
      if (!this.companies || this.companies.length === 0) {
        return "";
      }
      return this.companies.join(", ");
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false, width: "70px" },
        {
          text: this.$t("name_surname_phone"),
          value: "nameAndType",
          sortable: true,
        },
        { text: this.$t("address"), value: "address", sortable: false },
        { text: this.$t("time"), value: "time", sortable: false, width: "80px" },
        { text: this.$t("ordered"), value: "orders", sortable: false },
        { text: this.$t("total"), value: "total", sortable: false, width: "80px" },
        { text: this.$t("status"), value: "status", sortable: false, width: "120px" },
      ];
    },
    processedData() {
      // API vraća: { companyName: { date: [guests] } }
      // Treba nam: { date: [guests] } - spajamo sve kompanije
      const result = {};

      if (!this.reportData) return result;

      Object.values(this.reportData).forEach((companyData) => {
        Object.entries(companyData).forEach(([date, guests]) => {
          if (!result[date]) {
            result[date] = [];
          }
          result[date].push(...guests);
        });
      });

      // Sortiraj datume desc
      const sorted = {};
      Object.keys(result)
        .sort((a, b) => new Date(b) - new Date(a))
        .forEach((key) => {
          sorted[key] = result[key];
        });

      return sorted;
    },
    filteredData() {
      if (!this.processedData) return {};

      const filtered = {};

      Object.entries(this.processedData).forEach(([date, guests]) => {
        const filteredGuests = guests.filter((guest) => {
          const order = guest.orders_guest && guest.orders_guest[0];
          if (!order) return false;

          // Filter by status
          if (this.selectedStatus && this.selectedStatus.id !== 0) {
            if (
              order.state !== this.selectedStatus.alias &&
              order.state !== this.selectedStatus.name
            ) {
              return false;
            }
          }

          // Filter by delivery type
          if (this.selectedDeliveryType && this.selectedDeliveryType.id !== 3) {
            if (order.delivery_type !== this.selectedDeliveryType.id) {
              return false;
            }
          }

          return true;
        });

        if (filteredGuests.length > 0) {
          filtered[date] = filteredGuests;
        }
      });

      return filtered;
    },
  },
  watch: {
    isPrinting(isPrinting) {
      if (
        !isPrinting ||
        !this.isActiveTab ||
        !this.filteredData ||
        Object.keys(this.filteredData).length === 0
      ) {
        return;
      }

      this.printReportTable();
    },
    isExcelDownloading(isDownloading) {
      if (
        !isDownloading ||
        !this.isActiveTab ||
        !this.filteredData ||
        Object.keys(this.filteredData).length === 0
      ) {
        return;
      }

      this.exportReportExcel();
    },
    dateRange: {
      handler(newVal, oldVal) {
        if (newVal && newVal.length >= 2 && this.restaurantIds) {
          this.loadReportData();
        }
      },
      deep: true,
    },
    restaurantIds: {
      handler() {
        if (this.restaurantIds && this.dateRange && this.dateRange.length >= 2) {
          this.loadReportData();
        }
      },
    },
  },
  methods: {
    async loadReportData() {
      if (!this.restaurantIds || !this.dateRange || this.dateRange.length !== 2) {
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          date: this.dateRange[0],
          date2: this.dateRange[1],
          restaurants: this.restaurantIds,
        };

        const response = await reportService.getCompanyMultipleNoContract(params);
        this.reportData = response.data;

        // Extract unique companies
        const companiesSet = new Set();
        if (this.reportData && this.reportData.orders) {
          Object.keys(this.reportData.orders).forEach((date) => {
            const usersOrders = this.reportData.orders[date];
            usersOrders.forEach((user) => {
              if (user.company_name) {
                companiesSet.add(user.company_name);
              }
            });
          });
        }
        this.companies = Array.from(companiesSet).sort();
      } catch (error) {
        console.error("Error loading report data:", error);
        this.reportData = {};
        this.companies = [];
      } finally {
        this.isLoading = false;
      }
    },
    calculateTotal(item) {
      if (!item.orders_guest || !item.orders_guest.length) return 0;

      let total = 0;
      item.orders_guest.forEach((order) => {
        if (order.order_guest_transactions) {
          order.order_guest_transactions.forEach((transaction) => {
            total += transaction.price * transaction.quantity;
          });
        }
      });

      return total;
    },
    showOrderDetails(order, guest) {
      this.selectedOrder = order;
      this.selectedGuest = guest;
      this.orderDetailsDialog = true;
    },
    async changeOrderStatus(order, guest, newStatus) {
      if (order.state === newStatus) return;

      this.changingStatusOrderId = order.id;
      this.isLoading = true;

      // Send the entire order object with updated status
      const payload = {
        ...order,
        order_id: order.id,
        user_id: guest.id,
        status: newStatus,
        state: newStatus, // Assuming 'state' is the field to update
      };

      try {
        await reportService.changeOrderStatus(payload);
        // Update local state
        console.log(`Order ${order.id} status changed to ${newStatus}`);
        order.state = newStatus;
        if (order.order_guest_status) {
          order.order_guest_status.status = newStatus;
        }
        this.showSuccessSnackbar(this.$t("status_changed_successfully"));
        this.$emit("status-changed", { order, newStatus });
      } catch (error) {
        console.error("Error changing order status:", error);
        this.showErrorSnackbar(this.$t("status_change_error"));
        this.$emit("error", error);
      } finally {
        this.changingStatusOrderId = null;
        this.isLoading = false;
      }
    },
    printReportTable() {
      const tableItems = [];

      Object.keys(this.filteredData).forEach((date) => {
        const guests = this.filteredData[date];
        guests.forEach((guest) => {
          const total = this.calculateTotal(guest);
          const order =
            guest.orders_guest && guest.orders_guest[0] ? guest.orders_guest[0] : {};

          tableItems.push({
            num: order.id || "",
            nameAndType: `${guest.name} ${guest.lastname}\n${guest.phone}`,
            address: this.getAddressText(order),
            time: order.delivery_time ? order.delivery_time.substring(0, 5) : "",
            orders: this.getOrdersText(guest),
            total: `${total.toFixed(1)} ${
              order.restaurant?.settings?.price || this.restaurantCurrency
            }`,
            status: this.$t(order.state || ""),
          });
        });
      });

      const companyInfo = this.companySummary
        ? `<div style="text-align: center; margin-top: 8px;">${this.companySummary}</div>`
        : "";

      let footerHtml = "";
      if (this.selectedRestaurants && this.selectedRestaurants.length > 0) {
        footerHtml += `<div style="margin-top: 16px; font-size: 12px;"><strong>${this.$t(
          "restaurants"
        )}:</strong> ${this.restaurantNamesForFooter}</div>`;
      }
      if (this.companyNamesForFooter) {
        footerHtml += `<div style="margin-top: 8px; font-size: 12px;"><strong>${this.$t(
          "companies"
        )}:</strong> ${this.companyNamesForFooter}</div>`;
      }

      this.printDocument({
        pageTitle: this.$t("guest_report"),
        title: this.$t("guest_report"),
        subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
          this.dateRange[1]
        )}<h4 style="text-align: center">${this.restaurantSummary}</h4>${companyInfo}`,
        tables: [
          {
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
        afterTableHtml: footerHtml,
      });
    },
    exportReportExcel() {
      const tableItems = [];

      Object.keys(this.filteredData).forEach((date) => {
        const guests = this.filteredData[date];
        guests.forEach((guest) => {
          const total = this.calculateTotal(guest);
          const order =
            guest.orders_guest && guest.orders_guest[0] ? guest.orders_guest[0] : {};

          tableItems.push({
            num: order.id || "",
            nameAndType: `${guest.name} ${guest.lastname}\n${guest.phone}`,
            address: this.getAddressText(order),
            time: order.delivery_time ? order.delivery_time.substring(0, 5) : "",
            orders: this.getOrdersText(guest),
            total: `${total.toFixed(1)} ${
              order.restaurant?.settings?.price || this.restaurantCurrency
            }`,
            status: this.$t(order.state || ""),
          });
        });
      });

      const companyInfo = this.companySummary ? ` | ${this.companySummary}` : "";

      let footer = "";
      if (this.selectedRestaurants && this.selectedRestaurants.length > 0) {
        footer += `${this.$t("restaurants")}: ${this.restaurantNamesForFooter}`;
      }
      if (this.companyNamesForFooter) {
        if (footer) footer += " | ";
        footer += `${this.$t("companies")}: ${this.companyNamesForFooter}`;
      }

      this.downloadExcel({
        fileName: `${this.restaurantNames.replace(/,/g, "_") || "admin"}_izvestaj_${
          this.dateRange[0]
        }_${this.dateRange[1]}.xlsx`,
        sheets: [
          {
            name: this.$t("guest_report"),
            title: this.$t("guest_report"),
            subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
              this.dateRange[1]
            )} - ${this.restaurantSummary}${companyInfo}`,
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
            footer: footer,
          },
        ],
      });
    },
    getAddressText(order) {
      if (!order) return "";

      if (order.delivery_type === 0) {
        return `${this.$t("delivery_to_address")}\n${order.location?.address || ""}`;
      } else if (order.delivery_type === 1) {
        return `${this.$t("pickup")}\n${this.$t("in_restaurant")}`;
      } else if (order.delivery_type === 2) {
        return this.$t("consumption_in_restaurant");
      }
      return "";
    },
    getOrdersText(guest) {
      if (!guest.orders_guest || !guest.orders_guest.length) return "";

      let text = "";
      guest.orders_guest.forEach((order) => {
        if (order.order_guest_transactions) {
          order.order_guest_transactions.forEach((transaction) => {
            text += `${transaction.quantity}x ${transaction.food?.name || ""}: ${(
              transaction.price * transaction.quantity
            ).toFixed(1)}\n`;
          });
        }
      });
      return text.trim();
    },
  },
};
</script>
